import { InfoOutlined } from '@mui/icons-material';
import { Stack, Tooltip, Typography } from '@mui/material';
import { useContext, useState } from 'react';

import { ExternalInvitation } from '@/domain/external-invitation/entities/external-invitation.entity';
import { InviteCategory } from '@/domain/external-invitation/entities/invitation-category';
import { ScoreType, Sector } from '@/domain/impact/impact.entity';
import {
  EnvTopicValues,
  EssTopicValues,
} from '@/domain/impact-calculate/entities/calculate.entity';
import {
  EnvSection,
  EnvTopic,
} from '@/domain/impact-env/entities/question.entity';
import {
  EssSection,
  EssTopic,
} from '@/domain/impact-ess/entities/question.entity';
import { validateValueInType } from '@/domain/utils/validate-value-in-type';
import { CloseButton } from '@/ui/components/button/CloseButton';
import { QuizPanelLayout } from '@/ui/components/quiz-panel/QuizPanelLayout';
import { translate } from '@/ui/i18n';
import { Co2Quiz } from '@/ui/pages/app/components/quiz-panel/co2/Co2Quiz';
import { EnvQuiz } from '@/ui/pages/app/components/quiz-panel/env/EnvQuiz';
import { ESSQuiz } from '@/ui/pages/app/components/quiz-panel/ess/ESSQuiz';
import { ModalAssignExternal } from '@/ui/pages/app/components/stakeholders/ModalAssignExternal';
import { ModalAssignExternalPublic } from '@/ui/pages/app/components/stakeholders/ModalAssignExternalPublic';

import { InviteSubmitData } from '../stakeholders/assign-external/components/FormExternalInvite';
import { AssignButton } from './AssignButton';
import { Co2QuizFormData } from './co2/Co2QuizFormData';
import { ImpactQuestionContext } from './ImpactQuestionContext';

export function QuizPanel({
  projectId,
  quizScoreType,
  quizSubType,
  sector,
  externalCategories,
  externalExistingInvitations,
  onSubmitInvite,
  co2FormData,
  co2ExternalFormData,
  setIsQuizLayoutDisplayed,
  filterBySector,
  description,
  descriptionTootltip,
  essQuizLayout,
  envQuizLayout,
  enableExternal = true,
  enableExternalPublic,
  enableExternalAnswsersView,
  enableImportedAnswersView = false,
}: {
  projectId: number;
  quizScoreType: ScoreType; // co2 env ess
  quizSubType: string; // for co2 : category - for env+ess : topics
  sector?: Sector;
  externalCategories?: InviteCategory[];
  externalExistingInvitations: ExternalInvitation[];
  onSubmitInvite: (data: InviteSubmitData) => void;
  co2FormData: Co2QuizFormData | undefined;
  co2ExternalFormData: Co2QuizFormData | undefined;
  setIsQuizLayoutDisplayed?: React.Dispatch<React.SetStateAction<boolean>>;
  filterBySector?: boolean;
  description?: string;
  descriptionTootltip?: string;
  essQuizLayout: Record<EssTopic, { sections: Array<EssSection> }>;
  envQuizLayout: Record<EnvTopic, { sections: Array<EnvSection> }>;
  enableExternal?: boolean;
  enableExternalPublic: boolean;
  enableExternalAnswsersView: boolean;
  enableImportedAnswersView?: boolean;
}) {
  const [assignExternalModalOpen, setAssignExternalModalOpen] = useState(false);
  const [assignExternalPublicModalOpen, setAssignExternalPublicModalOpen] =
    useState(false);

  const { panelOpen, setPanelOpen } = useContext(ImpactQuestionContext);

  const onClickOpenAssign = (mode: 'normal' | 'public') => {
    if (mode === 'public') {
      // open modal for public
      setAssignExternalPublicModalOpen(true);
    } else {
      // open normal modal
      setAssignExternalModalOpen(true);
    }
  };

  const hideLayout = () => {
    if (setIsQuizLayoutDisplayed && setPanelOpen) {
      setIsQuizLayoutDisplayed(false);
      setTimeout(() => {
        setPanelOpen(false);
      }, 500);
    }
  };

  return (
    <>
      {sector && (
        <ModalAssignExternal
          open={assignExternalModalOpen}
          onClose={() => setAssignExternalModalOpen(false)}
          sector={sector}
          categories={externalCategories}
          existingInvitations={externalExistingInvitations}
          onSubmitInvite={onSubmitInvite}
        />
      )}
      <ModalAssignExternalPublic
        projectId={projectId}
        open={assignExternalPublicModalOpen}
        onClose={() => setAssignExternalPublicModalOpen(false)}
      />
      <QuizPanelLayout panelOpen={panelOpen} setPanelOpen={setPanelOpen}>
        <Stack direction={'row'} justifyContent={'space-between'}>
          <Stack direction={'row'} alignItems={'center'}>
            {setIsQuizLayoutDisplayed && (
              <CloseButton sx={{ mr: 1, ml: -2 }} onClick={hideLayout} />
            )}
            <QuizPanelTitle quizScoreType={quizScoreType} />
          </Stack>
          {quizScoreType === 'co2' && enableExternal && (
            <AssignButton
              enableExternalPublic={enableExternalPublic}
              invitationCount={externalExistingInvitations.length}
              onClickOpen={onClickOpenAssign}
            />
          )}
        </Stack>
        <QuizPanelDescription
          description={description}
          descriptionTootltip={descriptionTootltip}
        />
        <QuizPanelForm
          quizScoreType={quizScoreType}
          quizSubType={quizSubType}
          sector={sector}
          co2FormData={co2FormData}
          co2ExternalFormData={co2ExternalFormData}
          filterBySector={filterBySector}
          essQuizLayout={essQuizLayout}
          envQuizLayout={envQuizLayout}
          enableExternalPublic={enableExternalPublic}
          enableExternalAnswsersView={enableExternalAnswsersView}
          enableImportedAnswersView={enableImportedAnswersView}
        />
      </QuizPanelLayout>
    </>
  );
}

function QuizPanelTitle({ quizScoreType }: { quizScoreType: ScoreType }) {
  return (
    <Typography variant="h6">
      {translate(`Quiz.questions-${quizScoreType}`)}
    </Typography>
  );
}

function QuizPanelDescription({
  description,
  descriptionTootltip,
}: {
  description?: string;
  descriptionTootltip?: string;
}) {
  return (
    <>
      {description && (
        <Stack direction={'row'} marginTop={2}>
          <Typography sx={{ mr: 1 }}>{description}</Typography>
          <Tooltip title={descriptionTootltip}>
            <InfoOutlined />
          </Tooltip>
        </Stack>
      )}
    </>
  );
}

function QuizPanelForm({
  quizScoreType,
  quizSubType,
  sector,
  filterBySector,
  co2FormData,
  co2ExternalFormData,
  essQuizLayout,
  envQuizLayout,
  enableExternalPublic,
  enableExternalAnswsersView,
  enableImportedAnswersView,
}: {
  quizScoreType: ScoreType;
  quizSubType: string;
  sector?: Sector;
  filterBySector?: boolean;
  co2FormData: Co2QuizFormData | undefined;
  co2ExternalFormData: Co2QuizFormData | undefined;
  essQuizLayout: Record<EssTopic, { sections: Array<EssSection> }>;
  envQuizLayout: Record<EnvTopic, { sections: Array<EnvSection> }>;
  enableExternalPublic: boolean;
  enableExternalAnswsersView: boolean;
  enableImportedAnswersView: boolean;
}) {
  return (
    <>
      {quizScoreType === 'co2' && (
        <Co2Quiz
          form={co2FormData}
          externalForm={co2ExternalFormData}
          enableExternalPublic={enableExternalPublic}
          enableExternalAnswsersView={enableExternalAnswsersView}
          enableImportedAnswersView={enableImportedAnswersView}
        />
      )}
      {quizScoreType === 'env' && (
        <EnvQuiz
          topic={convertStringToEnvTopic(quizSubType)}
          sector={filterBySector ? sector : undefined}
          envQuizLayout={envQuizLayout}
        />
      )}
      {quizScoreType === 'ess' && (
        <ESSQuiz
          topic={convertStringToEssTopic(quizSubType)}
          sector={filterBySector ? sector : undefined}
          essQuizLayout={essQuizLayout}
        />
      )}
    </>
  );
}

function convertStringToEnvTopic(text: string): EnvTopic {
  return validateValueInType<EnvTopic>(text, EnvTopicValues);
}

function convertStringToEssTopic(text: string): EssTopic {
  return validateValueInType<EssTopic>(text, EssTopicValues);
}
